import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  
  PageRoute,
  pn,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import { ListFooter } from "components";
import {
  Permission,
  PermissionAction,
  SurveyFilter as SurveyFilterModel,
  SurveyListDto,
  SurveyListFilter,
  SurveyListResource,
  SurveyReportListDto,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { SurveyFilter } from "../SurveyFilter";
import { SurveyReportTable } from "./SurveyReportTable";
import { surveyReportPageRoute } from "../SurveyReports/SurveyReportPage";

const routeName = "surveyReports";

export const surveyReportListPageRoute: PageRoute = {
  handle: "Survey Reports",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const SurveyReportListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.Form;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<SurveyListFilter>({
    sortObjects: [{ property: pn<SurveyListDto>("id"), isDesc: true }],
  });
  const [initListFilter] = useState<SurveyListFilter>(listFilter);
  const [listItems, setListItems] = useState<SurveyReportListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<SurveyListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, loadList, applyPage, applyPageSize, applySortMethod, applySort, applySearch } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.surveyReports.getList().then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.surveyReports.getListTotal().then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    fetchResource();
    loadList(listFilter);
  }, []);

  const fetchResource = () => {
    ApiService.surveyReports.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<SurveyListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <SurveyFilter
        filter={listFilter.surveyFilter || ({} as SurveyFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.surveyFilter = filter;
            })
          )
        }
      />
    ),
  });

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{surveyReportListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() => navigate(surveyReportPageRoute.getPath({ id: 0, crudMode: "edit" }))}
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <SurveyReportTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              surveyReports={listItems}
              resource={resource}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
